import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { overallPerformanceRating } from "../config";

// Components
import TextTooltip from "sharedComponents/TextTooltip";
import TextWithLinks from "sharedComponents/TextWithLinks";

// Utils
import validateExplanationField from "utils/validateExplanationField";
import parseJsonSafely from "utils/parseJsonSafely";
import {formattedObj} from "utils/formattedObject";

// Hooks
import useDropoutSurveyField from "Hooks/useDropoutSurveyField";
import {findDOMNode} from "react-dom";


const Rating = ({fieldData, getAnswer, handleChangeSurveyCompletionStatus, mandatoryRefs, disabled = false}) => {
    const explanationRef = useRef();
    const ratingRef = useRef();
    const [showError, SetShowError] = useState('');
    const [ratingVal, SetRatingVal] = useState(null);
    const [showExplanation, SetShowExplanation] = useState(false);
    const { t } = useTranslation();
    const parsedFieldData = parseJsonSafely(fieldData?.type_content);
    const formattedData = formattedObj(fieldData?.type_content, parsedFieldData);

    const isLast = fieldData.label.includes(overallPerformanceRating);

    useDropoutSurveyField(ratingVal, '', handleChangeSurveyCompletionStatus);

    const handleChangeChoice = (id) => {
        if (ratingVal === id) {
            SetRatingVal(0);
        } else {
            SetRatingVal(id);
        }

        SetShowExplanation(!!fieldData.explanation && id !== 0 && (ratingVal !== id));

        if (fieldData.explanation && id !== 0) {
            const explanationValidation = validateExplanationField(explanationRef.current?.value, isLast);
            SetShowError(explanationValidation);
        }

        if (!ratingVal) {
            const fieldNode = findDOMNode(ratingRef.current);
            if (fieldData.mandatory) {
                if (Array.isArray(mandatoryRefs) && !mandatoryRefs.includes(fieldNode)) {
                    mandatoryRefs.unshift(fieldNode);
                }
            }
        }
    };

    const handleChangeExplanation = (e) => {
        SetShowError(validateExplanationField(e.target.value, isLast));
    };

    useEffect(() => {
        if (fieldData?.answers?.answer) {
            SetRatingVal(Number(fieldData?.answers?.answer));
            SetShowExplanation(!!fieldData?.answers?.explanation);
            explanationRef.current.value = fieldData?.answers?.explanation;
        }
    }, []);

    useEffect(() => {
        if (getAnswer.constructor !== Boolean) {
            getAnswer(prevState => ({
                ...prevState,
                [fieldData.id] : {answer: ratingVal || '', explanation: explanationRef.current.value}
            }));

            const explanationValidation = validateExplanationField(explanationRef.current?.value, isLast);
            explanationValidation && SetShowError(explanationValidation);

            (!!fieldData.mandatory && !ratingVal) ? SetShowError('Required') : SetShowError('');
        }
    }, [getAnswer, ratingVal]);

    useEffect(() => {
        const fieldNode = findDOMNode(ratingRef.current);
        if (fieldData.mandatory) {
            if (Array.isArray(mandatoryRefs) && !mandatoryRefs.includes(fieldNode)) {
                mandatoryRefs.push(fieldNode);
            }
        }
    }, [fieldData]);

    const choiceFieldColors = (id) => {
        switch (id) {
            case 5:
                return `strongly-agree${ratingVal === id ? '__active' : '__passive'}`;
            case 4:
                return `agree${ratingVal === id ? '__active' : '__passive'}`;
            case 3:
                return `neutral${ratingVal === id ? '__active' : '__passive'}`;
            case 2:
                return `disagree${ratingVal === id ? '__active' : '__passive'}`;
            case 1:
                return `strongly-disagree${ratingVal === id ? '__active' : '__passive'}`;
            default:
                return '';
        }
    }

    const fieldChoices = () => (
        formattedData.map(elem => {
            return (
                <div key={elem.id} className="rating-field__choice-box">
                    <TextTooltip message={elem.value}>
                        <div
                            className="rating-field__choice-value"
                            onClick={() => handleChangeChoice(elem.id)}
                        >
                            {t(elem.value)}
                        </div>
                    </TextTooltip>
                    <div
                        className={`rating-field__choice-checkbox ${choiceFieldColors(elem.id)}`}
                        onClick={() => handleChangeChoice(elem.id)}
                        style={disabled ? { pointerEvents: 'none' } : {}}
                    ></div>
                </div>
            )
        })
    );

    return (<div
        ref={ratingRef}
        className={`single_sect ${showError ? "invalid" : ""} ${isLast ? 'single_sect_last' : ''}`}
    >
        <h3 className="single_sect_primary_text">
            {fieldData.label}
            {!!fieldData.mandatory && <sup>*</sup>}
        </h3>
        <TextWithLinks className="single_sect_secondary_text">
            {fieldData.description}
        </TextWithLinks>

        <div className="rating-field">
            <div className="rating-field__choices">
                {fieldChoices()}
            </div>

            <div
                className={`input_field hidden_input_field ${showExplanation ? "show" : "hide"}`}
            >
                <input
                    ref={explanationRef}
                    onChange={handleChangeExplanation}
                    type="text"
                    placeholder={t("Explain your choice (optional)...")}
                    disabled={disabled}
                />
            </div>
        </div>

        <div className="error_sect">
            {t(showError)}
        </div>
    </div>);
}

export default React.memo(Rating);
